import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useCreateAdsActivity } from '../../shared/hooks/UseAdsActivities';
import { useSelectAllClient } from '../../shared/hooks/UseClient';
import { useSelectAllEmployee } from '../../shared/hooks/UseEmployee';
import Header from '../../components/template/Header';
import BreadCrumb from '../../components/template/BreadCrumb';
import Spinner from '../../components/layout/Spinner';
import toast from 'react-hot-toast';

const AdsActivityCreate = () => {
  const history = useHistory();
  const [clientData] = useSelectAllClient();
  const [employeeData] = useSelectAllEmployee();
  const [createActivity, { loading: creating }] = useCreateAdsActivity();

  const clients = clientData?.all_clients || [];
  const users = employeeData?.all_employees || [];

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    platform: 'GOOGLE',
    client: '',
    assigned_to: '',
    priority: 'MEDIUM',
    campaigns: [''], // Start with one empty campaign
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCampaignChange = (index, value) => {
    const newCampaigns = [...formData.campaigns];
    newCampaigns[index] = value;
    setFormData((prev) => ({ ...prev, campaigns: newCampaigns }));
  };

  const addCampaign = () => {
    setFormData((prev) => ({
      ...prev,
      campaigns: [...prev.campaigns, ''],
    }));
  };

  const removeCampaign = (index) => {
    if (formData.campaigns.length > 1) {
      const newCampaigns = formData.campaigns.filter((_, i) => i !== index);
      setFormData((prev) => ({ ...prev, campaigns: newCampaigns }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.client) {
      toast.error('Client is required');
      return;
    }
    if (!formData.assigned_to) {
      toast.error('Assigned user is required');
      return;
    }

    // Filter out empty campaigns
    const validCampaigns = formData.campaigns.filter((c) => c.trim());
    if (validCampaigns.length === 0) {
      toast.error('At least one campaign is required');
      return;
    }

    try {
      const activityData = {
        ...formData,
        campaigns: validCampaigns.map((name, index) => ({
          campaign_id: `manual-${Date.now()}-${index}`, // Generate a unique ID
          campaign_name: name.trim(),
          issue_type: 'LOW_PERFORMANCE', // Use valid enum value for manual entries
          benchmark: 0, // Default benchmark
          current_cpl: null,
          spend: 0,
          conversions: 0,
          status: 'PENDING',
          notes: '',
          // Removed action_taken since it's causing validation issues
        })),
      };

      console.log('Sending activity data:', activityData); // Debug log
      const result = await createActivity(activityData);
      console.log('API response:', result); // Debug log
      toast.success('Ads activity created successfully');
      history.push(`/ads-activities/${result.data._id}`);
    } catch (error) {
      console.error('Full create error:', error);
      console.error('Error response:', error?.response?.data);
      toast.error(
        'Failed to create ads activity: ' +
          (error?.response?.data?.message || error.message)
      );
    }
  };

  const handleCancel = () => {
    history.push('/ads-activities');
  };

  if (creating) return <Spinner />;

  return (
    <div className='pace-done'>
      <div>
        <Header />
        <BreadCrumb
          title='Create Ads Activity'
          mainLinkTitle='Ads Activities'
          mainLinkUrl='/ads-activities'
          activeLink='Create'
        />

        <div className='container-fluid' style={{ padding: '20px' }}>
          {/* Header */}
          <div className='row mb-4'>
            <div className='col-12'>
              <div className='d-flex justify-content-between align-items-center'>
                <div>
                  <h2 className='page-title'>Create Ads Activity</h2>
                  <p className='text-muted'>
                    Create a new ads campaign activity
                  </p>
                </div>
                <button
                  type='button'
                  className='btn btn-secondary'
                  onClick={handleCancel}
                >
                  <i className='fas fa-arrow-left me-2'></i>
                  Back to List
                </button>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className='row'>
            <div className='col-lg-8'>
              <div className='card'>
                <div className='card-header'>
                  <h5 className='card-title mb-0'>Activity Details</h5>
                </div>
                <div className='card-body'>
                  <form onSubmit={handleSubmit}>
                    {/* Basic Information */}
                    <div className='row mb-3'>
                      <div className='col-md-12'>
                        <label className='form-label'>Title *</label>
                        <input
                          type='text'
                          className='form-control'
                          value={formData.title}
                          onChange={(e) =>
                            handleInputChange('title', e.target.value)
                          }
                          placeholder='Enter activity title'
                          required
                        />
                      </div>
                    </div>

                    <div className='row mb-3'>
                      <div className='col-md-12'>
                        <label className='form-label'>Description</label>
                        <textarea
                          className='form-control'
                          rows='3'
                          value={formData.description}
                          onChange={(e) =>
                            handleInputChange('description', e.target.value)
                          }
                          placeholder='Enter activity description'
                        />
                      </div>
                    </div>

                    <div className='row mb-3'>
                      <div className='col-md-4'>
                        <label className='form-label'>Platform *</label>
                        <select
                          className='form-select'
                          value={formData.platform}
                          onChange={(e) =>
                            handleInputChange('platform', e.target.value)
                          }
                          required
                        >
                          <option value='GOOGLE'>Google Ads</option>
                          <option value='FACEBOOK'>Facebook Ads</option>
                        </select>
                      </div>

                      <div className='col-md-4'>
                        <label className='form-label'>Client *</label>
                        <select
                          className='form-select'
                          value={formData.client}
                          onChange={(e) =>
                            handleInputChange('client', e.target.value)
                          }
                          required
                        >
                          <option value=''>Select Client</option>
                          {clients.map((client) => (
                            <option key={client._id} value={client._id}>
                              {client.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className='col-md-4'>
                        <label className='form-label'>Assigned To *</label>
                        <select
                          className='form-select'
                          value={formData.assigned_to}
                          onChange={(e) =>
                            handleInputChange('assigned_to', e.target.value)
                          }
                          required
                        >
                          <option value=''>Select User</option>
                          {users.map((user) => (
                            <option key={user._id} value={user._id}>
                              {user.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className='row mb-4'>
                      <div className='col-md-4'>
                        <label className='form-label'>Priority</label>
                        <select
                          className='form-select'
                          value={formData.priority}
                          onChange={(e) =>
                            handleInputChange('priority', e.target.value)
                          }
                        >
                          <option value='LOW'>Low</option>
                          <option value='MEDIUM'>Medium</option>
                          <option value='HIGH'>High</option>
                          <option value='URGENT'>Urgent</option>
                        </select>
                      </div>
                    </div>

                    {/* Campaigns Section */}
                    <div className='mb-4'>
                      <div className='d-flex justify-content-between align-items-center mb-3'>
                        <h6 className='mb-0'>Campaigns *</h6>
                        <button
                          type='button'
                          className='btn btn-sm btn-outline-primary'
                          onClick={addCampaign}
                        >
                          <i className='fas fa-plus me-1'></i>
                          Add Campaign
                        </button>
                      </div>

                      {formData.campaigns.map((campaign, index) => (
                        <div key={index} className='row mb-2'>
                          <div className='col-md-10'>
                            <input
                              type='text'
                              className='form-control'
                              value={campaign}
                              onChange={(e) =>
                                handleCampaignChange(index, e.target.value)
                              }
                              placeholder={`Campaign ${index + 1} name`}
                            />
                          </div>
                          <div className='col-md-2'>
                            {formData.campaigns.length > 1 && (
                              <button
                                type='button'
                                className='btn btn-outline-danger btn-sm w-100'
                                onClick={() => removeCampaign(index)}
                              >
                                <i className='fas fa-trash'></i>
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Submit Buttons */}
                    <div className='d-flex gap-2'>
                      <button
                        type='submit'
                        className='btn btn-primary'
                        disabled={creating}
                      >
                        {creating ? (
                          <>
                            <span
                              className='spinner-border spinner-border-sm me-2'
                              role='status'
                            ></span>
                            Creating...
                          </>
                        ) : (
                          <>
                            <i className='fas fa-save me-2'></i>
                            Create Activity
                          </>
                        )}
                      </button>
                      <button
                        type='button'
                        className='btn btn-secondary'
                        onClick={handleCancel}
                        disabled={creating}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            {/* Side Panel with Tips */}
            <div className='col-lg-4'>
              <div className='card'>
                <div className='card-header'>
                  <h6 className='card-title mb-0'>
                    <i className='fas fa-lightbulb text-warning me-2'></i>
                    Tips
                  </h6>
                </div>
                <div className='card-body'>
                  <div className='small text-muted'>
                    <div className='mb-3'>
                      <strong>Title:</strong> Use a descriptive title that
                      explains the issue or goal.
                    </div>
                    <div className='mb-3'>
                      <strong>Campaigns:</strong> Add all campaigns that need
                      attention for this activity.
                    </div>
                    <div className='mb-3'>
                      <strong>Priority:</strong>
                      <ul className='mt-1 ps-3'>
                        <li>
                          <strong>Urgent:</strong> Critical issues affecting
                          performance
                        </li>
                        <li>
                          <strong>High:</strong> Important optimizations
                        </li>
                        <li>
                          <strong>Medium:</strong> Regular maintenance
                        </li>
                        <li>
                          <strong>Low:</strong> Nice-to-have improvements
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdsActivityCreate;
